import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import DetailedPageLayout from '../components/DetailedPageLayout';
import CustomAreaChart from '../../../components/Charts/CustomAreaChart';
import CustomBarChart from '../../../components/Charts/CustomBarChart';
import CustomDonutChart from '../../../components/Charts/CustomDonutChart';
import StatCard from '../components/ui/StatCard';
import SectionCard from '../components/ui/SectionCard';
import dashboardAPI from '../../../services/dashboardService';
import { Stethoscope, Activity, CalendarCheck } from 'lucide-react';

const STATUS_LABEL_KEYS = ['pending', 'confirmed', 'completed', 'cancelled', 'no_show'];

const DetailedMedicalPage = ({ isSubView = false, filters: externalFilters, onFilterChange: externalOnFilterChange }) => {
    const { t } = useTranslation();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [localFilters, setLocalFilters] = useState({ timeRange: '30days', category: 'all' });

    const filters = externalFilters || localFilters;

    const fetchStats = useCallback(async (newFilters = filters) => {
        setLoading(true);
        try {
            const response = await dashboardAPI.getMedicalStats(newFilters);
            setStats(response.data);
        } catch (error) {
            console.error('Failed to fetch medical stats', error);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    const onFilterChange = externalOnFilterChange || ((newFilters) => {
        setLocalFilters(newFilters);
        fetchStats(newFilters);
    });

    useEffect(() => {
        fetchStats(filters);
    }, [fetchStats, filters]);

    const statusData = useMemo(() => {
        if (!stats?.appointmentsByStatus) return [];
        return Object.entries(stats.appointmentsByStatus).map(([key, value]) => ({
            name: STATUS_LABEL_KEYS.includes(key.toLowerCase())
                ? t(`systemDashboard.medical.statusLabels.${key.toLowerCase()}`)
                : key,
            value
        }));
    }, [stats?.appointmentsByStatus, t]);

    const graphData = stats?.graph_data || [];

    if (loading || !stats) {
        return <div className="sd-loading-message">{t('systemDashboard.detailed.loading')}</div>;
    }

    return (
        <DetailedPageLayout
            title={t('systemDashboard.detailed.titles.medical')}
            tab="medical"
            onFilterChange={onFilterChange}
            filters={filters}
            isSubView={isSubView}
        >
            <div className="sd-grid sd-grid--kpi-users">
                <StatCard label={t('systemDashboard.medical.totalAppointments')} value={stats.totalAppointments} icon={CalendarCheck} color="bg-rose-500" />
                <StatCard label={t('systemDashboard.medical.activeClinics')} value={stats.activeClinics} icon={Stethoscope} color="bg-teal-500" />
                <StatCard
                    label={t('systemDashboard.medical.completionRate')}
                    value={`${(stats?.completionRate || 0).toFixed(1)}%`}
                    icon={Activity}
                    color="bg-amber-500"
                />
            </div>

            <div className="sd-grid sd-grid--split">
                <SectionCard title={t('systemDashboard.medical.appointmentVolume')}>
                    <CustomBarChart data={graphData} title="" />
                </SectionCard>
                <SectionCard title={t('systemDashboard.medical.appointmentStatus')}>
                    <CustomDonutChart
                        data={statusData}
                        totalValue={stats.totalAppointments}
                        title=""
                    />
                </SectionCard>
            </div>

            <SectionCard title={t('systemDashboard.medical.appointmentsOverTime')}>
                <CustomAreaChart data={graphData} color="#ef4444" title="" />
            </SectionCard>
        </DetailedPageLayout>
    );
};

export default DetailedMedicalPage;
